import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Target, ChevronRight, Building2, Palette, CreditCard, Search, RefreshCw, Zap, Settings, Clock, Globe, Sparkles, Users, Calendar } from 'lucide-react';
import { useOrganizations, CachedImage, SentryTestButton, AnimatedDemoSection } from '@footypicks/shared';
import { usePublicWorldCupConfig } from '@footypicks/shared/hooks/usePublicWorldCupConfig';
import { updateThemeColor } from '@footypicks/shared/utils/format';
import { restoreDefaultBranding } from '@footypicks/shared/utils/favicon';
import { SHOW_TOURNAMENT_CAMPAIGN } from '../config/tournamentCampaign';

const LOGO_URL = `${import.meta.env.VITE_SUPABASE_URL || 'https://ibztzxgdbluqzisqkiry.supabase.co'}/storage/v1/object/public/Organization%20Logos/full-text-primary.png`;

export function HomePage() {
  const navigate = useNavigate();
  const { organizations, loading: orgsLoading } = useOrganizations();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSentryTest, setShowSentryTest] = useState(false);
  const wcConfig = usePublicWorldCupConfig();

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check for password recovery tokens in URL and redirect to reset password page
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');

    // If this is a password recovery link, redirect to the reset password page
    if (type === 'recovery' && accessToken) {
      console.log('Password recovery detected, redirecting to reset-password page');
      navigate('/reset-password', { replace: true });
      return;
    }
  }, [navigate]);

  // Check for Sentry test mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const testParam = params.get('test');
    console.log('Query params:', { test: testParam, fullUrl: window.location.href });

    if (testParam === 'sentry') {
      console.log('Sentry test mode activated!');
      setShowSentryTest(true);
    }
  }, []);

  // Set default theme color and branding for the main landing page
  useEffect(() => {
    updateThemeColor('#4A1D96');
    restoreDefaultBranding();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4A1D96] via-[#5B2BA6] to-[#3D1780]">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <header className="bg-white/5 backdrop-blur-sm border-b border-white/10">
          <div className="pt-safe bg-white/5">
            <div className="container mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <Link to="/">
                  <CachedImage
                    src={LOGO_URL}
                    alt="Footy Picks"
                    size="small"
                    className="h-16 w-auto"
                    loading="eager"
                    decoding="async"
                  />
                </Link>
                <nav className="hidden md:flex items-center gap-8">
                  <a href="#organizations" className="text-white/90 hover:text-[#F4C430] transition-colors font-medium">
                    Find Your League
                  </a>
                  {SHOW_TOURNAMENT_CAMPAIGN && (
                    <a href="#worldcup-for-orgs" className="text-[#F4C430] hover:text-white transition-colors font-medium inline-flex items-center gap-1">
                      <Trophy className="w-4 h-4" />
                      World Cup
                    </a>
                  )}
                  <a href="#features" className="text-white/90 hover:text-[#F4C430] transition-colors font-medium">
                    Competition Types
                  </a>
                  <a href="#for-organizations" className="text-white/90 hover:text-[#F4C430] transition-colors font-medium">
                    For Organisations
                  </a>
                </nav>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-6 pt-[calc(9rem+env(safe-area-inset-top))] pb-12 sm:pt-[calc(10rem+env(safe-area-inset-top))] sm:pb-16 md:pt-[calc(11rem+env(safe-area-inset-top))] md:pb-24 lg:pt-[calc(13rem+env(safe-area-inset-top))] lg:pb-32 min-h-[85vh] sm:min-h-0 flex items-center">
        <div className="max-w-4xl mx-auto text-center w-full">
          {SHOW_TOURNAMENT_CAMPAIGN && wcConfig && (
            <a
              href="/worldcup"
              className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-[#F4C430]/15 border border-[#F4C430]/40 text-[#F4C430] hover:bg-[#F4C430]/25 transition-all text-sm font-semibold"
            >
              <Trophy className="w-4 h-4" />
              <span>The World Cup Predictor is live</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          )}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-4 sm:mb-6 leading-tight">
            Football Competitions for <span className="text-[#F4C430]">Your Organisation</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-8 sm:mb-12 leading-relaxed">
            {SHOW_TOURNAMENT_CAMPAIGN
              ? 'Run engaging prediction leagues, Last Man Standing knockouts and full World Cup competitions with your own branding. We handle the game mechanics, scoring, and payments so you can focus on your community.'
              : 'Run engaging prediction leagues and Last Man Standing knockouts with your own branding. We handle the game mechanics, scoring, and payments so you can focus on your community.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pb-8 sm:pb-0">
            <a href="#organizations" className="bg-[#F4C430] text-[#4A1D96] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFD700] transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center gap-2">
              Browse Organisations
              <ChevronRight className="w-5 h-5" />
            </a>
            <a href="#for-organizations" className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center gap-2">
              For Organisations
              <Building2 className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* organizations Browser */}
      <section id="organizations" className="bg-white/5 backdrop-blur-sm py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-8">
            Find Your League
          </h2>
          <p className="text-xl text-white/70 text-center mb-12 max-w-3xl mx-auto">
            Browse competitions from various organisations. Join your workplace, sports club, or community league.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search organisations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-full py-4 pl-12 pr-6 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F4C430] focus:border-transparent"
              />
            </div>
          </div>

          {/* organizations Grid */}
          {orgsLoading ? (
            <div className="text-center text-white/60">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4C430]"></div>
              <p className="mt-4">Loading organisations...</p>
            </div>
          ) : filteredOrganizations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {filteredOrganizations.map((org) => (
                <Link
                  key={org.id}
                  to={`/${org.slug}`}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 hover:border-[#F4C430]/50 transition-all hover:scale-105 transform group"
                >
                  {org.logoUrl && (
                    <div className="mb-4 flex items-center justify-center h-20">
                      <CachedImage
                        src={org.logoUrl}
                        alt={org.name}
                        size="small"
                        className="max-h-full max-w-full object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#F4C430] transition-colors">
                    {org.name}
                  </h3>
                  {org.description && (
                    <p className="text-white/70 text-sm leading-relaxed">
                      {org.description}
                    </p>
                  )}
                  {/*org.is_free_to_play && (
                    <div className="mt-3 inline-block bg-[#F4C430]/20 text-[#F4C430] px-3 py-1 rounded-full text-xs font-semibold">
                      Free to Play
                    </div>
                  )*/}
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center text-white/60 py-12">
              <p className="text-xl">No organisations found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section - Competition Types */}
      <section id="features" className="py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-16">
            Competition Types
          </h2>
          <div className={`grid md:grid-cols-2 ${SHOW_TOURNAMENT_CAMPAIGN ? 'lg:grid-cols-3' : ''} gap-8 max-w-6xl mx-auto`}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 transform">
              <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#4A1D96]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Predictor Leagues</h3>
              <p className="text-white/70 leading-relaxed">
                Predict match outcomes and exact scores. Earn points for accuracy and climb the leaderboard throughout the season.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 transform">
              <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Trophy className="w-8 h-8 text-[#4A1D96]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Last Man Standing</h3>
              <p className="text-white/70 leading-relaxed">
                Pick one team to win each week. One wrong pick and you're out. Who will be the last one standing?
              </p>
            </div>

            {SHOW_TOURNAMENT_CAMPAIGN && (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#F4C430]/40 hover:bg-white/15 transition-all hover:scale-105 transform relative">
                <div className="absolute -top-3 right-6 bg-[#F4C430] text-[#4A1D96] text-xs font-black px-3 py-1 rounded-full uppercase tracking-wide">
                  New
                </div>
                <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Globe className="w-8 h-8 text-[#4A1D96]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">World Cup Predictor</h3>
                <p className="text-white/70 leading-relaxed">
                  Predict the entire tournament — group stage scores, knockout bracket, Golden Boot and your champion. Run it as a mini-league with friends, family or your whole organisation.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* For organizations Section */}
      <section id="for-organizations" className="bg-white/5 backdrop-blur-sm py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-8">
            For Organisations
          </h2>
          <p className="text-xl text-white/70 text-center mb-16 max-w-3xl mx-auto">
            Launch your own branded football competitions in minutes. Perfect for workplaces, sports clubs, fan groups, and communities.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 transform">
              <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Palette className="w-8 h-8 text-[#4A1D96]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Your Branding</h3>
              <p className="text-white/70 leading-relaxed">
                Custom colors, logo, and welcome messages. Your competition, your identity.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 transform">
              <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Trophy className="w-8 h-8 text-[#4A1D96]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Go</h3>
              <p className="text-white/70 leading-relaxed">
                We handle all the game mechanics, scoring, leaderboards, and technical infrastructure.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all hover:scale-105 transform">
              <div className="bg-[#F4C430] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <CreditCard className="w-8 h-8 text-[#4A1D96]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Payments Included</h3>
              <p className="text-white/70 leading-relaxed">
                Integrated Stripe payments for entry fees. We manage transactions, you focus on your community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* World Cup For organizations - Flagship Section (campaign-gated for future internationals) */}
      {SHOW_TOURNAMENT_CAMPAIGN && (
        <section
          id="worldcup-for-orgs"
          className="relative py-24 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))] overflow-hidden"
        >
          {/* Decorative gold glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#F4C430]/10 via-transparent to-[#F4C430]/5 pointer-events-none" />
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#F4C430]/10 rounded-full blur-3xl pointer-events-none" />

          <div className="container mx-auto px-6 relative">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-[#F4C430]/15 border border-[#F4C430]/40 text-[#F4C430] text-sm font-bold uppercase tracking-wider">
                <Sparkles className="w-4 h-4" />
                Flagship Offering
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                Run Your Own <span className="text-[#F4C430]">World Cup</span> Competition
              </h2>
              <p className="text-xl text-white/80 leading-relaxed">
                The World Cup is the single biggest engagement opportunity of the four-year cycle.
                Launch a fully-branded predictor for your workplace, club, pub, fan group or supporter
                community — and turn the tournament into <em className="text-[#F4C430] not-italic font-semibold">your</em> event.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Tournament-Long Engagement</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  One sign-up keeps your members coming back for every group game, knockout round and the final. 104 fixtures of conversation and competition.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Palette className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Your Brand, Centre Stage</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Custom colours, logo, welcome copy and share graphics. Members see your identity, not ours.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Built for the Format</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Group standings, full knockout bracket, Golden Boot and tournament winner — all scored automatically using our proven engine.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Mini-Leagues Inside</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Members can spin up sub-leagues for their team, department, pub or family — multiplying engagement without extra admin.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Entry Fees & Prize Pots</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Optional Stripe-powered entry fees with automatic prize pot calculation. Run it free, or fund a real prize.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#F4C430]/30 hover:bg-white/15 hover:border-[#F4C430]/60 transition-all">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Zero Setup Headache</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Fixtures, scoring, leaderboards and reminders all handled. You launch it; we run it.
                </p>
              </div>
            </div>

            <p className="text-center text-lg text-white/80 italic mb-8 max-w-2xl mx-auto">
              World Cups only come around every four years. Don't let it pass you by!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:hello@footypicks.co.uk?subject=World%20Cup%20Competition%20Enquiry"
                className="bg-[#F4C430] text-[#4A1D96] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFD700] transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center gap-2"
              >
                <Trophy className="w-5 h-5" />
                Set Up Your World Cup Competition
              </a>
              {wcConfig && (
                <a
                  href="/worldcup"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all flex items-center justify-center gap-2"
                >
                  <Globe className="w-5 h-5" />
                  See a Live Example
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/*{SHOW_TOURNAMENT_CAMPAIGN && wcConfig && (
        <section
          id="worldcup-play"
          className="bg-white/5 backdrop-blur-sm py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]"
        >
          <div className="container mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-4">
              Predict the World Cup
            </h2>
            <p className="text-xl text-white/70 text-center mb-16 max-w-3xl mx-auto">
              From the opening fixture to the final whistle — one entry, the whole tournament.
            </p>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Group Stage</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Predict every score across the group stage. Standings update live.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Knockout Bracket</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Build your bracket from R32 to the final. Bonus points for nailing the route to glory.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="bg-[#F4C430] w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6 text-[#4A1D96]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Golden Boot & Champion</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Pick your tournament top scorer and overall winner for big-bonus points.
                </p>
              </div>
            </div>
            <div className="text-center">
              <a
                href="/worldcup"
                className="inline-flex items-center gap-2 bg-[#F4C430] text-[#4A1D96] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFD700] transition-all transform hover:scale-105 shadow-2xl"
              >
                Play the World Cup Predictor
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      )}*/}


      <section id="automation" className="py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-8">
            Ditch the Spreadsheet
          </h2>
          <p className="text-xl text-white/70 text-center mb-16 max-w-3xl mx-auto">
            Stop spending hours updating Excel files every week. FootyPicks automates everything so you can enjoy the competition.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="bg-emerald-500 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Live Scores</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Scores update automatically from official sources. No manual data entry required.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="bg-red-500 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Auto Knockouts</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Players eliminated automatically when their team loses. Instant leaderboard updates.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Flexible Rules</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Configure missed pick behavior, end-game scenarios, entry limits, and more.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
              <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Auto Reminders</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Email and WhatsApp reminders sent automatically before deadlines. No chasing required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* See It In Action Section */}
      <section id="demo" className="bg-white/5 backdrop-blur-sm py-20 scroll-mt-[calc(5.5rem+env(safe-area-inset-top))]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-8">
            See It In Action
          </h2>
          <p className="text-xl text-white/70 text-center mb-16 max-w-3xl mx-auto">
            Simple, intuitive, and engaging. Here's how FootyPicks works.
          </p>
          <AnimatedDemoSection />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#F4C430] to-[#FFD700] py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-[#4A1D96] mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-[#4A1D96]/80 mb-8 max-w-2xl mx-auto">
            Find your organisation above to join an existing league, or contact us to set up your own branded competition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <a href="#organizations" className="inline-block bg-[#4A1D96] text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-[#5B2BA6] transition-all transform hover:scale-105 shadow-2xl">
              Browse Organisations
            </a>
            {SHOW_TOURNAMENT_CAMPAIGN && (
              <a href="mailto:hello@footypicks.co.uk?subject=World%20Cup%20Competition%20Enquiry" className="inline-flex items-center justify-center gap-2 bg-[#4A1D96] text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-[#5B2BA6] transition-all transform hover:scale-105 shadow-2xl">
                <Trophy className="w-5 h-5" />
                Set Up Your World Cup
              </a>
            )}
            <a href="mailto:hello@footypicks.co.uk?subject=League%20Setup%20Enquiry" className="inline-block bg-white text-[#4A1D96] px-10 py-4 rounded-full font-bold text-lg hover:bg-white/90 transition-all transform hover:scale-105 shadow-2xl">
              Set Up Your League
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A1050] py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <CachedImage
              src={LOGO_URL}
              alt="Footy Picks"
              size="thumbnail"
              className="h-12 w-auto opacity-80 grayscale"
              loading="lazy"
              decoding="async"
            />
            <div className="flex gap-8 text-white/60">
              <a href="#" className="hover:text-[#F4C430] transition-colors">Privacy</a>
              <a href="#" className="hover:text-[#F4C430] transition-colors">Terms</a>
              <a href="mailto:hello@footypicks.co.uk" className="hover:text-[#F4C430] transition-colors">Contact</a>
            </div>
            <p className="text-white/60 text-sm">
              © 2025 Footy Picks. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {showSentryTest && <SentryTestButton />}
    </div>
  );
}
